// ============================================================================
// AUDIT TRAIL SERVICE — Logs who did what and when
// ============================================================================
import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase-config';

/**
 * Log an audit event
 * @param {string} firmId 
 * @param {object} event - { userId, userName, action, category, details, entityId, entityName }
 */
export const logAuditEvent = async (firmId, event) => {
  if (!firmId) { console.warn('Audit: no firmId provided'); return { success: false }; }
  try {
    await addDoc(collection(db, 'auditLogs'), {
      firmId,
      userId: event.userId || '',
      userName: event.userName || 'Unknown',
      action: event.action || '',
      category: event.category || '',
      details: event.details || '',
      entityId: event.entityId || '',
      entityName: event.entityName || '',
      metadata: event.metadata || {},
      ip: '',
      timestamp: Timestamp.now(),
      createdAt: new Date().toISOString()
    });
    return { success: true };
  } catch (e) {
    console.error('Audit log error:', e);
    return { success: false, error: e.message };
  }
};

/**
 * Get audit logs for a firm
 * Uses a simple query (firmId only) then sorts client-side to avoid needing Firestore composite indexes
 */
export const getAuditLogs = async (firmId, filters = {}) => {
  if (!firmId) return { success: false, logs: [], error: 'No firmId' };
  try {
    // Simple query — only filter by firmId, no orderBy (avoids composite index requirement)
    const q = query(
      collection(db, 'auditLogs'),
      where('firmId', '==', firmId)
    );
    const snap = await getDocs(q);
    let logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Sort client-side by timestamp (newest first)
    logs.sort((a, b) => {
      const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return timeB - timeA;
    });

    // Apply client-side filters
    if (filters.category) logs = logs.filter(l => l.category === filters.category);
    if (filters.userId) logs = logs.filter(l => l.userId === filters.userId);
    if (filters.startDate) {
      const start = new Date(filters.startDate);
      logs = logs.filter(l => {
        const t = l.timestamp?.toDate ? l.timestamp.toDate() : new Date(l.createdAt);
        return t >= start;
      });
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59);
      logs = logs.filter(l => {
        const t = l.timestamp?.toDate ? l.timestamp.toDate() : new Date(l.createdAt);
        return t <= end;
      });
    }

    // Apply limit after sorting and filtering
    if (filters.limit) logs = logs.slice(0, filters.limit);

    return { success: true, logs };
  } catch (e) {
    console.error('Get audit logs error:', e);
    return { success: false, logs: [], error: e.message };
  }
};

/**
 * Get audit logs for a specific entity (e.g. a case)
 */
export const getEntityAuditLogs = async (firmId, entityId) => {
  try {
    const snap = await getDocs(query(
      collection(db, 'auditLogs'),
      where('firmId', '==', firmId),
      where('entityId', '==', entityId)
    ));
    const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    logs.sort((a, b) => {
      const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return timeB - timeA;
    });
    return { success: true, logs };
  } catch (e) {
    return { success: false, logs: [], error: e.message };
  }
};

// Audit action constants
export const AUDIT_ACTIONS = {
  CREATED: 'created',
  UPDATED: 'updated',
  DELETED: 'deleted',
  VIEWED: 'viewed',
  EXPORTED: 'exported',
  ASSIGNED: 'assigned',
  STATUS_CHANGED: 'status_changed',
  LOGGED_IN: 'logged_in',
  LOGGED_OUT: 'logged_out',
  INVITED: 'invited',
  SUSPENDED: 'suspended',
  REACTIVATED: 'reactivated',
  PAYMENT_RECEIVED: 'payment_received',
  DOCUMENT_UPLOADED: 'document_uploaded',
  REMINDER_SENT: 'reminder_sent'
};

export const AUDIT_CATEGORIES = {
  CASE: 'case',
  CLIENT: 'client',
  DOCUMENT: 'document',
  INVOICE: 'invoice',
  TASK: 'task',
  TEAM: 'team',
  SETTINGS: 'settings',
  BILLING: 'billing',
  AUTH: 'auth',
  COURT: 'court',
  COMMUNICATION: 'communication'
};
